import { useState } from 'react';

import { Loader2, Send } from 'lucide-react';

import { useGetSandbox } from '@/api/getSandbox';
import { useGetSandboxes } from '@/api/getSandboxes';
import { AppTooltip } from '@/components/appTooltip';
import { usePopup } from '@/components/confirmation';
import { PermissionCheck } from '@/components/permissionCheck';
import { Button } from '@/components/ui/button';
import { useGame } from '@/hooks/useGame';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { toaster } from '@/lib/toaster';
import { Permissions } from '@/models/permission';
import { type SandboxPublishStatus } from '@/models/sandbox';
import { useSandboxContext } from '@/providers';

import { SandboxPublishSummaryDialog } from './sandboxPublishSummaryDialog';
import {
  type PublishSandboxResponse,
  usePublishSandbox,
} from '../api/publishSandbox';

/**
 * Only includes counts that are greater than 0.
 */
const buildPendingCounts = (status: SandboxPublishStatus): string[] => {
  const parts: string[] = [];
  if (status.ready_items_count - status.ready_season_passes_count > 0) {
    parts.push(
      `${status.ready_items_count - status.ready_season_passes_count} catalog item(s)`,
    );
  }
  if (status.ready_snapshots_count > 0) {
    parts.push(`${status.ready_snapshots_count} snapshot(s)`);
  }
  if (status.ready_gifts_count > 0) {
    parts.push(`${status.ready_gifts_count} gift(s)`);
  }
  if (status.ready_season_passes_count > 0) {
    parts.push(`${status.ready_season_passes_count} season pass(es)`);
  }
  return parts;
};

/**
 * Publish entry for the current Sandbox, rendered at the end of the breadcrumb trail
 * by the `sandbox` route's `action` handle — so it only ever mounts inside
 * SandboxProvider.
 */
export const SandboxPublishButton = () => {
  const [publishResult, setPublishResult] =
    useState<PublishSandboxResponse | null>(null);

  const { selectedSandbox } = useSandboxContext();
  const game = useGame();
  const { showPopup } = usePopup();

  const { data: sandboxes } = useGetSandboxes({ game_code: game.game_code });

  const currentSandbox = sandboxes?.sandboxes.find(
    (sandbox) => sandbox.code === selectedSandbox,
  );

  // GET /v1/sandbox/{id} is gated on sandboxes:read (admin-api router.go), and this
  // button renders on every Sandbox-scoped page — so without the code the query
  // would 403 on every navigation.
  //
  // Known limitation: a Grant carrying sandboxes:publish without sandboxes:read
  // can't reach publish_status at all (the endpoint demands read), so the button
  // stays disabled on "No pending changes" even when there are some. Fixing it
  // needs publish_status on the auth-only sandbox list, not a wider gate here.
  const canReadSandbox = usePermissionCheck({
    oneOf: [Permissions.sandboxes.read],
  });

  const { data: sandboxDetail } = useGetSandbox(
    { sandboxId: currentSandbox?.id ?? '' },
    { enabled: !!currentSandbox?.id && canReadSandbox },
  );

  // Narrowed rather than a bare boolean: every use of the counts is gated on it.
  const pending = sandboxDetail?.publish_status?.has_pending
    ? sandboxDetail.publish_status
    : null;

  const publishMutation = usePublishSandbox();

  const handlePublish = async () => {
    if (!currentSandbox || !pending) {
      toaster.info('No sandbox selected');
      return;
    }

    await showPopup({
      title: 'Confirm Publish',
      content: (
        <div className="space-y-2">
          <p>
            Are you sure you want to publish sandbox{' '}
            <strong>{currentSandbox.name}</strong>?
          </p>
          <ul className="text-sm text-muted-foreground">
            This will publish :
            {buildPendingCounts(pending).map((part) => (
              <li key={part} className="list-disc list-inside font-bold">
                {part}
              </li>
            ))}
          </ul>
        </div>
      ),
      confirmText: 'Publish',
      cancelText: 'Cancel',
      onConfirm: async () => {
        // publishSandbox already toasts the outcome.
        setPublishResult(
          await publishMutation.mutateAsync({
            sandboxId: currentSandbox.id,
            gameCode: game.game_code,
          }),
        );
      },
    });
  };

  return (
    <PermissionCheck oneOf={[Permissions.sandboxes.publish]}>
      <AppTooltip
        title={
          pending
            ? `Ready to publish: ${buildPendingCounts(pending).join(', ')}`
            : 'No pending changes'
        }
      >
        {/* A disabled button fires no pointer events, so the tooltip needs its own
            trigger — and "nothing to publish" is exactly when it is worth reading. */}
        <span>
          <Button
            size="sm"
            variant={pending ? 'default' : 'ghost'}
            disabled={!pending || publishMutation.isPending}
            onClick={() => {
              void handlePublish();
            }}
          >
            {publishMutation.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Send />
            )}
            Publish
          </Button>
        </span>
      </AppTooltip>
      {publishResult && (
        <SandboxPublishSummaryDialog
          open={!!publishResult}
          onOpenChange={(open) => {
            if (!open) setPublishResult(null);
          }}
          response={publishResult}
        />
      )}
    </PermissionCheck>
  );
};
